// src/pages/BrandDetail/components/BrandSummarySection.js
import { MessageSquare, Package, Sprout, Percent, Leaf, Recycle, Info, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import './BrandSummarySection.css';

// Placeholder data for fields not available in API
const PLACEHOLDER = {
  tagline: "Leading Korean Clean Beauty Brand",
  description: "This brand is dedicated to creating innovative beauty products with natural ingredients for healthy, beautiful skin.",
  badges: [
    { icon: Leaf, label: "Sustainably Cultivated", color: "emerald" },
    { icon: Recycle, label: "Recyclable Packaging", color: "blue" }
  ],
  ecoScore: 3 // out of 5
};

const BrandSummarySection = ({ brandDetails }) => {
  const { translate } = useLanguage();

  if (!brandDetails) return null;

  const { brand_name, logo_url, brand_metrics } = brandDetails;

  // Get health category badge color
  const getHealthBadgeClass = (category) => {
    const categoryLower = category?.toLowerCase() || '';
    if (categoryLower.includes('strong') || categoryLower.includes('건강')) {
      return 'health-badge-strong';
    }
    if (categoryLower.includes('moderate') || categoryLower.includes('보통')) {
      return 'health-badge-moderate';
    }
    return 'health-badge-weak';
  };

  return (
    <section className="glass-card brand-summary-section">
      {/* Background Decoration */}
      <div className="brand-summary-bg-decoration"></div>

      <div className="brand-summary-content">
        {/* Left side: Brand Info */}
        <div className="brand-info-section">
          {/* Logo with glow effect */}
          <div className="brand-logo-glow-container">
            <div className="brand-logo-glow"></div>
            <img
              src={logo_url}
              alt={`${brand_name} logo`}
              className="brand-logo-image"
              onError={(e) => {
                e.target.src = '/placeholder-logo.png';
              }}
            />
          </div>

          {/* Brand details */}
          <div className="brand-details">
            <div className="brand-name-row">
              <h1 className="brand-name font-serif">{brand_name}</h1>
              <span className={`health-badge ${getHealthBadgeClass(brand_metrics?.brand_health_category)}`}>
                {brand_metrics?.brand_health_category || 'Unknown'}
              </span>
            </div>

            <p className="brand-tagline">{PLACEHOLDER.tagline}</p>

            {/* Sustainability badges */}
            <div className="sustainability-badges">
              {PLACEHOLDER.badges.map((badge, index) => (
                <span key={index} className={`sustainability-badge badge-${badge.color}`}>
                  <badge.icon className="badge-icon" />
                  {badge.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right side: Metrics Grid */}
        <div className="metrics-grid">
          {/* Reviews Metric */}
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon-wrapper metric-icon-blue">
                <MessageSquare className="metric-icon" />
              </div>
              <span className="metric-label">{translate('brandDetailPage.metrics.reviews') || 'Reviews'}</span>
            </div>
            <div className="metric-value">
              {brand_metrics?.total_reviews?.toLocaleString() || 0}
            </div>
            <div className="metric-trend metric-trend-up">
              <TrendingUp className="trend-icon" /> Growing
            </div>
          </div>

          {/* Products Metric */}
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon-wrapper metric-icon-purple">
                <Package className="metric-icon" />
              </div>
              <span className="metric-label">{translate('brandDetailPage.metrics.products') || 'Products'}</span>
            </div>
            <div className="metric-value">
              {brand_metrics?.product_num || 0}
            </div>
            <div className="metric-subtext">Active items</div>
          </div>

          {/* Eco Score Metric (Placeholder) */}
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon-wrapper metric-icon-green">
                <Sprout className="metric-icon" />
              </div>
              <span className="metric-label">Eco Score</span>
            </div>
            <div className="metric-value-with-max">
              <span className="metric-value">{PLACEHOLDER.ecoScore}</span>
              <span className="metric-max">/ 5</span>
            </div>
            <div className="eco-score-dots">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`eco-dot ${i <= PLACEHOLDER.ecoScore ? 'eco-dot-filled' : 'eco-dot-empty'}`}
                />
              ))}
            </div>
          </div>

          {/* Avg Discount Metric */}
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon-wrapper metric-icon-rose">
                <Percent className="metric-icon" />
              </div>
              <span className="metric-label">{translate('brandDetailPage.metrics.avgDiscountRate') || 'Avg Discount'}</span>
            </div>
            <div className="metric-value">
              {brand_metrics?.avg_discount_rate?.toFixed(1) || 0}%
            </div>
            <div className="metric-subtext">Market Avg</div>
          </div>
        </div>
      </div>

      {/* Bottom: Description */}
      <div className="brand-description-section">
        <Info className="description-icon" />
        <p className="brand-description">{PLACEHOLDER.description}</p>
      </div>
    </section>
  );
};

export default BrandSummarySection;
